import React from 'react';
import PropTypes from 'prop-types';
import Cropper from 'react-easy-crop';
import { Upload } from 'lucide-react';

// Mixins / Modules
import AwsUploader from '../../../../../modules/aws_uploader';

// Base
import Avatar from '../../../base/avatar';
import Button from '../../../base/button';
import Modal from '../../../base/modal';
import ProgressBar from '../../../base/progress_bar';

// Related Components
import getCroppedImageFile from '@/components/v6/base/avatar_editor/get_cropped_image_file';

export default class AvatarUpload extends React.Component {
  static propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    onError: PropTypes.func,
  }

  static defaultProps = {
    value: '',
    onError: () => { },
  }

  constructor(props) {
    super(props);

    this.state = {
      avatarFile: null,
      avatar: props.value,
      preCroppedAvatar: '',
      progress: 0,
      isOpen: false,
      uploading: false,
      crop: { x: 0, y: 0 },
      zoom: 1,
      croppedAreaPixels: null,
      isCropping: false,
    };

    this.previewUrl = null;
  }

  componentWillUnmount() {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }
  }

  handleAvatarChange = (e) => {
    const input = e.target;
    const avatarFile = input.files && input.files[0];

    if (avatarFile) {
      const reader = new FileReader();

      reader.onload = (event) => {
        this.setState({
          avatarFile,
          preCroppedAvatar: event.target.result,
          isOpen: true,
          crop: { x: 0, y: 0 },
          zoom: 1,
          croppedAreaPixels: null,
        });
      };

      reader.readAsDataURL(avatarFile);
      input.value = '';
    }
  }

  handleCropComplete = (_croppedArea, croppedAreaPixels) => {
    this.setState({ croppedAreaPixels });
  }

  handleCrop = async (e) => {
    e && e.preventDefault();

    const {
      avatarFile,
      preCroppedAvatar,
      croppedAreaPixels,
    } = this.state;

    if (!avatarFile || !preCroppedAvatar || !croppedAreaPixels) {
      return;
    }

    this.setState({ isCropping: true });

    try {
      const newFile = await getCroppedImageFile({
        imageSrc: preCroppedAvatar,
        croppedAreaPixels,
        fileName: avatarFile.name,
        fileType: avatarFile.type,
      });

      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
      }

      this.previewUrl = URL.createObjectURL(newFile);

      this.setState({
        avatar: this.previewUrl,
        isOpen: false,
      });

      this.handleUpload(newFile);
    } finally {
      this.setState({ isCropping: false });
    }
  }

  handleUpload = (file) => {
    const { onChange, onError } = this.props;

    this.setState({ uploading: true }, () => {
      new AwsUploader()
        .presignerPath('/aws/public_presigner', 'uploads/avatars')
        .onProgress((progress) => {
          this.setState({ progress });
        })
        .onSuccess(({ url }) => {
          if (this.previewUrl) {
            URL.revokeObjectURL(this.previewUrl);
            this.previewUrl = null;
          }

          this.setState({
            uploading: false,
            avatar: url,
          }, () => onChange(url));
        })
        .onError((error) => {
          this.setState({
            uploading: false,
          }, () => onError(error));
        })
        .upload(file);
    });
  }

  handleClose = () => {
    this.setState({ isOpen: false });
  }

  handleClear = () => {
    const { onChange } = this.props;

    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
    }

    this.setState({
      avatar: undefined,
      progress: 0,
    }, () => onChange(''));
  }

  render() {
    const {
      avatar,
      progress,
      isOpen,
      uploading,
      preCroppedAvatar,
      crop,
      zoom,
      isCropping,
    } = this.state;

    return (
      <React.Fragment>
        <div className='flex vertical-align margin-bottom-25'>
          <div className='flex'>
            <div>
              <Avatar {...{
                key: avatar,
                src: avatar || `${gon.HUBSPOT_ASSETS_URL}/oomple/assets/app/v5/sign_up/purple_avatar.png`,
                className: 'width-70 height-70',
              }} />
            </div>

            <div className='vertical-align flex-col margin-left-15 justify-space-between padding-5 align-items-flex-start'>
              <div className='sm-font tertiary-color'>
                Profile Picture
              </div>

              {
                !uploading &&
                <div>
                  {
                    avatar &&
                    <div className='upload-clear'>
                      <div className='upload-clear-hover'>
                        <Button {...{
                          text: 'Remove',
                          size: 'sm',
                          color: 'secondary-danger',
                          onClick: this.handleClear,
                        }} />
                      </div>
                    </div>
                  }

                  {
                    !avatar &&
                    <div>
                      <label
                        htmlFor='files'
                        className='btn is-secondary flex flex-row vertical-align'
                      >
                        <Upload size='16' />

                        <div className='margin-left-5'>
                          Upload image
                        </div>
                      </label>

                      <input {...{
                        id: 'files',
                        value: '',
                        onChange: this.handleAvatarChange,
                        type: 'file',
                        accept: 'image/*',
                        className: 'hide',
                      }} />
                    </div>
                  }
                </div>
              }

              {
                uploading &&
                <div className='width-420 margin-bottom-15'>
                  <ProgressBar {...{
                    value: progress,
                    height: 10,
                  }} />
                </div>
              }
            </div>
          </div>

          <Modal {...{
            size: 'sm',
            isOpen,
            onClose: this.handleClose,
            onCancel: this.handleClose,
            showX: false,
            className: 'width-350 height-420',
          }}>
            <div className='flex flex-col margin-top-20'>
              <div
                style={{
                  position: 'relative',
                  width: 300,
                  height: 300,
                  margin: '0 auto',
                  overflow: 'hidden',
                  background: '#111',
                }}
              >
                {
                  preCroppedAvatar &&
                  <Cropper
                    image={preCroppedAvatar}
                    crop={crop}
                    zoom={zoom}
                    aspect={1}
                    cropShape='round'
                    showGrid={false}
                    onCropChange={(nextCrop) => this.setState({ crop: nextCrop })}
                    onZoomChange={(nextZoom) => this.setState({ zoom: nextZoom })}
                    onCropComplete={this.handleCropComplete}
                  />
                }
              </div>

              <div style={{ width: 300, margin: '10px auto 0' }}>
                <input
                  type='range'
                  min='1'
                  max='3'
                  step='0.1'
                  value={zoom}
                  onChange={(event) => this.setState({ zoom: Number(event.target.value) })}
                  style={{ width: '100%' }}
                />
              </div>

              <div className='right-align margin-right-25'>
                <Button {...{
                  onClick: this.handleCrop,
                  text: isCropping ? 'Cropping...' : 'Crop',
                  disabled: isCropping,
                }} />
              </div>
            </div>
          </Modal>
        </div>
      </React.Fragment>
    )
  }
}
